import { useCallback, useEffect, useMemo, useState } from "react";
import { Text, View } from "react-native";
import {
  HeartHandshake,
  Phone,
  UserPlus,
  UserRound,
  Users,
} from "lucide-react-native";
import {
  ActionBanner,
  colors,
  PrimaryButton,
  MoonPhoneField,
  MoonSelectField,
  semanticColors,
  SecondaryButton,
  TextActionButton,
  TextField,
} from "@louise/moon-ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";
import { useTranslations } from "@src/packages/i18n";
import { useUpdatePatient } from "@src/packages/patients/hooks/usePatient";
import { GroupedCard } from "../components";
import type { EmergencyContact as EmergencyContactData } from "../types";

const RELATIONSHIP_OPTIONS = [
  { value: "Partner", labelKey: "you.medical_details.relationship_partner" },
  { value: "Spouse", labelKey: "you.medical_details.relationship_spouse" },
  { value: "Parent", labelKey: "you.medical_details.relationship_parent" },
  { value: "Child", labelKey: "you.medical_details.relationship_child" },
  { value: "Sibling", labelKey: "you.medical_details.relationship_sibling" },
  { value: "Friend", labelKey: "you.medical_details.relationship_friend" },
  {
    value: "Colleague",
    labelKey: "you.medical_details.relationship_colleague",
  },
  { value: "Other", labelKey: "you.medical_details.relationship_other" },
] as const;

type Props = {
  contact: EmergencyContactData | null;
};

type FieldErrors = {
  name?: string;
  phone?: string;
};

/**
 * A small local draft prevents partial contact details from being persisted.
 * The API accepts an emergency contact only as an atomic name + phone update,
 * so the user gets field-level feedback before a request is sent.
 */
export default function EmergencyContact({ contact }: Props) {
  const t = useTranslations();
  const updatePatient = useUpdatePatient();
  const relationshipOptions = RELATIONSHIP_OPTIONS.map((option) => ({
    label: t(option.labelKey),
    value: option.value,
  }));
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(contact?.name ?? "");
  const [phone, setPhone] = useState(contact?.phone ?? "");
  const [relationship, setRelationship] = useState(contact?.relationship ?? "");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    setName(contact?.name ?? "");
    setPhone(contact?.phone ?? "");
    setRelationship(contact?.relationship ?? "");
    setFieldErrors({});
  }, [contact?.name, contact?.phone, contact?.relationship]);

  const draftContact = useMemo(
    () => toEmergencyContact(name, phone, relationship),
    [name, phone, relationship],
  );
  const summary = [
    draftContact?.name,
    draftContact?.phone,
    relationshipOptions.find(
      (option) => option.value === draftContact?.relationship,
    )?.label ?? draftContact?.relationship,
  ]
    .filter(Boolean)
    .join(" · ");
  const hasChanges = !sameEmergencyContact(draftContact, contact);

  const resetDraft = useCallback(() => {
    setName(contact?.name ?? "");
    setPhone(contact?.phone ?? "");
    setRelationship(contact?.relationship ?? "");
    setFieldErrors({});
    setSaveFailed(false);
    setOpen(false);
  }, [contact?.name, contact?.phone, contact?.relationship]);

  const toggleOpen = useCallback(() => {
    if (open) {
      resetDraft();
      return;
    }
    setSaveFailed(false);
    setFieldErrors({});
    setOpen(true);
  }, [open, resetDraft]);

  const removeContact = useCallback(() => {
    setName("");
    setPhone("");
    setRelationship("");
    setFieldErrors({});
    setSaveFailed(false);
  }, []);

  const save = useCallback(async () => {
    if (!hasChanges || updatePatient.isPending) return;

    if (draftContact) {
      const nextErrors: FieldErrors = {};
      if (!draftContact.name) {
        nextErrors.name = t("you.medical_details.emergency_name_required");
      }
      if (!draftContact.phone) {
        nextErrors.phone = t("you.medical_details.emergency_phone_required");
      }
      if (Object.keys(nextErrors).length > 0) {
        setFieldErrors(nextErrors);
        return;
      }
    }

    setFieldErrors({});
    setSaveFailed(false);
    try {
      await updatePatient.mutateAsync({
        emergencyContactName: draftContact?.name ?? null,
        emergencyContactPhone: draftContact?.phone ?? null,
        emergencyContactRelationship: draftContact?.relationship || null,
      });
      setOpen(false);
    } catch {
      setSaveFailed(true);
    }
  }, [draftContact, hasChanges, t, updatePatient]);

  return (
    <View>
      <GroupedCard
        style={{
          overflow: "hidden",
        }}
      >
        <Accordion
          type="multiple"
          value={open ? ["contact"] : []}
          onValueChange={(next: string[]) => {
            if (next.includes("contact") !== open) toggleOpen();
          }}
        >
          <AccordionItem value="contact">
            <AccordionTrigger
              accessibilityLabel={
                summary
                  ? `${t("you.medical_details.emergency_contact_label")}: ${summary}`
                  : t("you.medical_details.emergency_contact_placeholder")
              }
              // The visible title only: the accessibility label carries the
              // contact's name and number, which never go to analytics.
              analyticsLabel={
                summary
                  ? t("you.medical_details.emergency_contact_label")
                  : t("you.medical_details.emergency_contact_placeholder")
              }
              action="emergency_contact_row"
              disabled={updatePatient.isPending}
              className="min-h-[52px] items-center gap-2.5 px-3.5 py-3"
              style={{ opacity: updatePatient.isPending ? 0.6 : 1 }}
            >
              <View
                style={{
                  width: 28,
                  height: 28,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {summary ? (
                  <HeartHandshake
                    size={15}
                    color={colors.textLight}
                    strokeWidth={1.65}
                  />
                ) : (
                  <UserPlus
                    size={15}
                    color={colors.textLight}
                    strokeWidth={1.65}
                  />
                )}
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: "500",
                    color: colors.text,
                  }}
                >
                  {summary
                    ? t("you.medical_details.emergency_contact_label")
                    : t("you.medical_details.emergency_contact_placeholder")}
                </Text>
                {summary && !open ? (
                  <Text
                    className="font-dm-sans"
                    numberOfLines={1}
                    style={{
                      marginTop: 2,
                      fontSize: 12,
                      color: colors.textLight,
                    }}
                  >
                    {summary}
                  </Text>
                ) : null}
              </View>
            </AccordionTrigger>

            <AccordionContent className="pb-0">
              <View
                style={{
                  gap: 12,
                  padding: 12,
                  borderTopWidth: 1,
                  borderTopColor: semanticColors.border.subtle,
                  backgroundColor: semanticColors.surface.subtle,
                }}
              >
                <ContactNameField
                  label={t("you.medical_details.emergency_name_label")}
                  value={name}
                  placeholder={t(
                    "you.medical_details.emergency_name_placeholder",
                  )}
                  error={fieldErrors.name}
                  disabled={updatePatient.isPending}
                  onChangeText={(next) => {
                    setName(next);
                    setFieldErrors((errors) => ({
                      ...errors,
                      name: undefined,
                    }));
                  }}
                />
                <MoonPhoneField
                  label={t("you.medical_details.emergency_phone_label")}
                  labelIcon={Phone}
                  value={phone}
                  error={fieldErrors.phone}
                  onChange={(next) => {
                    setPhone(next);
                    setFieldErrors((errors) => ({
                      ...errors,
                      phone: undefined,
                    }));
                  }}
                  onClear={phone ? () => setPhone("") : undefined}
                  disabled={updatePatient.isPending}
                />
                <MoonSelectField
                  label={t("you.medical_details.emergency_relationship_label")}
                  labelIcon={Users}
                  value={relationship || null}
                  options={relationshipOptions}
                  placeholder={t(
                    "you.medical_details.emergency_relationship_placeholder",
                  )}
                  onChange={setRelationship}
                  disabled={updatePatient.isPending}
                />
                {saveFailed ? (
                  <ActionBanner
                    variant="important"
                    message={t(
                      "you.medical_details.emergency_contact_save_failed",
                    )}
                  />
                ) : null}
                {contact ? (
                  <TextActionButton
                    label={t("you.medical_details.remove_emergency_contact")}
                    action="you.medical_details.remove_emergency_contact"
                    tone="destructive"
                    size="sm"
                    disabled={updatePatient.isPending}
                    onPress={removeContact}
                    style={{
                      alignSelf: "flex-start",
                      paddingHorizontal: 0,
                      paddingVertical: 2,
                    }}
                  />
                ) : null}
                <View style={{ flexDirection: "row", gap: 10, paddingTop: 2 }}>
                  <SecondaryButton
                    label={t("you.medical_details.cancel")}
                    onPress={resetDraft}
                    disabled={updatePatient.isPending}
                    flex={1}
                  />
                  <PrimaryButton
                    label={t("you.medical_details.save")}
                    onPress={save}
                    disabled={!hasChanges || updatePatient.isPending}
                    flex={1}
                  />
                </View>
              </View>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </GroupedCard>
    </View>
  );
}

function ContactNameField({
  label,
  value,
  placeholder,
  error,
  disabled,
  onChangeText,
}: {
  label: string;
  value: string;
  placeholder: string;
  error?: string;
  disabled?: boolean;
  onChangeText: (value: string) => void;
}) {
  return (
    <TextField
      label={label}
      labelIcon={UserRound}
      error={error}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      autoComplete="name"
      returnKeyType="done"
      disabled={disabled}
    />
  );
}

function toEmergencyContact(
  name: string,
  phone: string,
  relationship: string,
): EmergencyContactData | null {
  const contact = {
    name: name.trim(),
    phone: phone.trim(),
    relationship: relationship.trim(),
  };
  return contact.name || contact.phone || contact.relationship ? contact : null;
}

function sameEmergencyContact(
  first: EmergencyContactData | null,
  second: EmergencyContactData | null,
): boolean {
  return (
    first?.name === second?.name &&
    first?.phone === second?.phone &&
    first?.relationship === second?.relationship
  );
}
